import type {
    PartialProfilePermissions,
    PermissionCheck,
    PermissionProps,
    PermissionProvider,
    ReadOnlyProfilePermissions,
} from "@qendo/permissions";
import { type CycleData, type EmergencyInstructionData, type ProfileData, ProfileRole } from "@qendo/profile";
import type { ContactData, ContactPayload } from "@qendo/profile/contacts";
import type {
    InvitationPayload,
    ProfileInvitationData,
    ShareCreatePayload,
    ShareResponsePayload,
} from "@qendo/profile/invitation";
import type { TeamLike, TeamMemberData, TeamMemberUpdatePayload } from "@qendo/profile/team";
import type { AgeRange, BloodType, Gender, ListData } from "@qendo/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { compareAsc } from "date-fns";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LoadingMask } from "~/components/feedback/LoadingMask";
import { Only } from "~/components/permission/Only";
import { CurrentCycleData } from "~/entities/profile/current-cycle-data";
import { EmergencyInstruction } from "~/entities/profile/emergency-instruction";
import type { Goals } from "~/entities/profile/goals";
import { Permissions } from "~/entities/profile/permissions";
import { Profile } from "~/entities/profile/profile";
import { type ApiError, resolveStoreMethod, useApi } from "~/providers/ApiProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { GalleryProvider } from "~/providers/GalleryProvider";
import { JournalProvider } from "~/providers/JournalProvider";
import { MedicalProvider } from "~/providers/MedicalProvider";
import { ReportProvider } from "~/providers/ReportProvider";
import { TrackableProvider } from "~/providers/TrackableProvider";
import { useUser } from "~/providers/UserProvider";
import { filled } from "~/util";
import { deleteQuery, mergeQuery, removeQuery } from "~/util/api";
import { ProfileKey, profileStorage } from "~/util/secure-storage";
import { OptionsProvider } from "./OptionsProvider";

export interface ProfileUpdatePayload {
    name?: string;
    phone?: string;
    cycle?: number;
    age_range?: AgeRange | null;
    country?: string | null;
    postcode?: string | null;
    blood_type?: BloodType;
    gender?: Gender | null;
    medical_instructions?: string;
    photograph?: string | null;
}

export interface EmergencyInstructionPayload {
    id?: string;
    description: string;
    photograph_ids?: string[];
}

const EmptyProfile = Profile.from({
    id: "",
    name: "",
    avatar: "",
});

declare interface ProfileState extends PermissionProvider {
    profile: Profile;
    profile_id: string | null;
    active: boolean;
    can_update: boolean;
    hydrated: boolean;
    instructions: EmergencyInstruction[];
    cycle: CurrentCycleData;
    is_primary: boolean;
    invitations: ProfileInvitationData[];
    team: TeamMemberData[];
    contacts: ContactData[];
    changeProfile(profile_id?: string | null | undefined): void;
    updateProfile(payload: ProfileUpdatePayload): Promise<void>;
    updateCycleSettings(payload: CycleData): Promise<void>;
    refreshCycle(): Promise<void>;
    saveGoals(goals: Goals): Promise<void>;
    updateAvatar(id: string | null): Promise<void>;
    storeInstruction(payload: EmergencyInstructionPayload): Promise<void>;
    destroyInstruction(id: string): Promise<void>;
    refreshInstructions(): Promise<void>;
    canUpdateRole(role?: ProfileRole | null): boolean;
    createShare(payload: ShareCreatePayload): Promise<ShareResponsePayload | null>;
    storeContact(payload: ContactPayload): Promise<string | null>;
    destroyContact(id: string): Promise<void>;
    refreshContacts(): Promise<void>;
    storeInvitation(payload: InvitationPayload): Promise<string | null>;
    destroyInvitation(id: string): Promise<void>;
    refreshInvitations(): Promise<void>;
    canUpdateTeamMember(id: string): boolean;
    updateTeamMember(payload: TeamMemberUpdatePayload): Promise<void>;
    destroyTeamMember(id: string): Promise<void>;
    refreshTeam(): Promise<void>;
    findTeam(team_member_id: string): TeamLike | null;
}

export const ProfileContext = createContext<ProfileState>({
    profile: EmptyProfile,
    profile_id: null,
    active: false,
    can_update: false,
    hydrated: false,
    instructions: [],
    cycle: new CurrentCycleData(),
    is_primary: false,
    invitations: [],
    team: [],
    contacts: [],
    changeProfile: (_profile_id) => {
        return;
    },
    updateProfile: async (_payload) => {
        return;
    },
    updateCycleSettings: async (_payload) => {
        return;
    },
    refreshCycle: async () => {
        return;
    },
    saveGoals: async (_goals) => {
        return;
    },
    updateAvatar: async (_id: string | null) => {
        return;
    },
    storeInstruction: async (_payload) => {
        return;
    },
    destroyInstruction: async (_id) => {
        return;
    },
    refreshInstructions: async () => {
        return;
    },
    createShare: async (_payload) => null,
    storeContact: async (_payload) => null,
    destroyContact: async (_id) => {
        return;
    },
    refreshContacts: async () => {
        return;
    },
    canUpdateRole: (_role) => false,
    storeInvitation: async (_payload) => null,
    destroyInvitation: async (_id) => {
        return;
    },
    refreshInvitations: async () => {
        return;
    },
    canUpdateTeamMember: (_id) => false,
    updateTeamMember: async (_payload) => {
        return;
    },
    destroyTeamMember: async (_id) => {
        return;
    },
    refreshTeam: async () => {
        return;
    },
    findTeam: (_team_member_id) => null,
    can: (_ability, _section) => false,
    canAny: (_ability, _section) => false,
    cannot: (_ability, _section) => true,
    cannotAny: (_ability, _section) => true,
});

export function useProfile() {
    const value = useContext(ProfileContext);
    if (!value) {
        throw new Error("useProfile must be wrapped in a <ProfileProvider />");
    }
    return value;
}

export function ProfileProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();
    const { client } = useApi();
    const { setError } = useFeedback();
    const { user, active, team_ids, hasTeam, primary_profile, isPrimaryTeam } = useUser();
    const [loading, setLoading] = useState(false);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [hydrated, setHydrated] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const profile_id = useMemo(() => profile?.id ?? null, [profile]);
    const permissions = useMemo<Permissions>(() => new Permissions(profile?.permissions), [profile]);

    const can = useCallback<PermissionCheck>((ability, section) => permissions.can(ability, section), [permissions]);
    const canAny = useCallback<PermissionCheck>(
        (ability, section) => permissions.canAny(ability, section),
        [permissions],
    );
    const cannot = useCallback<PermissionCheck>(
        (ability, section) => permissions.cannot(ability, section),
        [permissions],
    );
    const cannotAny = useCallback<PermissionCheck>(
        (ability, section) => permissions.cannotAny(ability, section),
        [permissions],
    );

    const clearProfile = () => {
        profile && setProfile(null);
        loaded && setLoaded(false);
        queryClient.clear();
    };

    const changeProfile = (to?: string | null | undefined): void => {
        const team = to ?? team_ids[0] ?? primary_profile ?? null;

        if (active && team && hasTeam(team)) {
            loadProfile(team);
        } else if (active && to) {
            changeProfile();
        } else {
            clearProfile();
        }
    };

    const loadProfile = (to?: string | null | undefined) => {
        if (!to) {
            throw new Error("Cannot load empty profile.");
        }
        if (hasTeam(to)) {
            if (loading) {
                return;
            }

            if (profile_id && profile_id !== to) {
                clearProfile();
            }

            setLoading(true);

            client()
                .get(`/profiles/${to}`)
                .then(({ data }: AxiosResponse<ProfileData>) => {
                    setProfile(Profile.from(data));
                    loaded || setLoaded(true);
                })
                .catch((error: ApiError) => {
                    switch (error.status) {
                        case 401:
                            setError("You must be authenticated to access your profile.");
                            break;
                        case 403:
                            setError("You no longer have access to this team.");
                            break;
                        case 404:
                            setError("This team does not exist.");
                            break;
                    }
                    if (primary_profile) {
                        isPrimaryTeam(to)
                            ? setError("There was an error loading your profile")
                            : changeProfile(primary_profile);
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            changeProfile();
        }
    };

    const refreshProfile = () => {
        if (active && hydrated) {
            profile_id ? loadProfile(profile_id) : changeProfile();
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(refreshProfile): API
    // biome-ignore lint/correctness/useExhaustiveDependencies(clearProfile): API
    useEffect(() => {
        if (active && !hydrated) {
            Promise.allSettled([profileStorage.getItem<Profile>(ProfileKey.profile).then(setProfile)]).finally(() => {
                setHydrated(true);
            });
        } else if (hydrated && active && !loaded && (team_ids.length > 0 || primary_profile)) {
            refreshProfile();
        } else if (hydrated && profile_id && !active) {
            clearProfile();
        }
    }, [active, hydrated, loaded, profile_id, team_ids, primary_profile]);

    useEffect(() => {
        hydrated && profileStorage.setItem(ProfileKey.profile, profile);
    }, [profile, hydrated]);

    const can_update = useMemo(() => profile_id !== null && can("update", "profile"), [profile_id, can]);

    const updateProfile = async (payload: ProfileUpdatePayload) => {
        if (!can_update) {
            return;
        }

        const current = Profile.from(profile as Profile).fill(payload);

        if (current.dirty()) {
            await client()
                .patch(`profiles/${profile_id}`, payload)
                .then(({ data }: AxiosResponse<ProfileData>) => {
                    setProfile(Profile.from(data));
                });
        }
    };

    const saveGoals = async (goals: Goals) => {
        if (!can_update) {
            return;
        }

        await client()
            .patch(`profiles/${profile_id}/goals`, goals)
            .then(({ data }: AxiosResponse<ProfileData>) => {
                setProfile(Profile.from(data));
            });
    };

    const updateAvatar = async (id: string | null) => {
        if (!can_update) {
            return;
        }

        if (
            Profile.from(profile as Profile)
                .fill({ photograph_id: id })
                .dirty("photograph_id")
        ) {
            await (id
                ? client().patch(`profiles/${profile_id}/avatar`, { photograph: id })
                : client().delete(`profiles/${profile_id}/avatar`)
            ).then(({ data }: AxiosResponse<ProfileData>) => {
                const updated = Profile.from(data);
                setProfile(updated);
            });
        }
    };

    const cycleKey = ["cycle", profile_id];
    const cycleQuery = useQuery({
        queryKey: cycleKey,
        queryFn: async () => {
            if (profile_id) {
                return await client()
                    .get(`profiles/${profile_id}/cycle`)
                    .then((response: AxiosResponse<CurrentCycleData>) => {
                        return CurrentCycleData.from(response.data);
                    })
                    .catch(() => new CurrentCycleData());
            }
            return new CurrentCycleData();
        },
        placeholderData: new CurrentCycleData(),
        staleTime: 1000 * 60 * 60, // 1 hour
        refetchOnMount: true,
    });

    const refreshCycle = async () => {
        await queryClient.invalidateQueries({ queryKey: cycleKey, refetchType: "all" });
    };

    const storeCycleMutation = useMutation({
        mutationFn: async (payload: CycleData) => {
            return await client()
                .patch(`profiles/${profile_id}/cycle`, payload)
                .then((response: AxiosResponse<CurrentCycleData>) => {
                    return CurrentCycleData.from(response.data);
                });
        },
        onSuccess: (cycle: CurrentCycleData) => {
            queryClient.setQueryData(cycleKey, cycle);
        },
    });
    const updateCycleSettings = async (payload: CycleData) => {
        if (!can_update) {
            return;
        }

        await storeCycleMutation.mutateAsync(payload);
    };

    const cycle = useMemo(() => {
        const data = CurrentCycleData.from(cycleQuery.data ?? {});

        if (data.periods.length > 0) {
            data.periods.sort((a, b) => compareAsc(a.start_at, b.start_at));
        }

        return data;
    }, [cycleQuery.data]);

    const instructionKey = ["emergency_instructions", profile_id];

    const instructionQuery = useQuery({
        queryKey: instructionKey,
        queryFn: async () => {
            if (can("view", "emergency")) {
                return await client()
                    .get(`profiles/${profile_id}/instructions`)
                    .then((response: AxiosResponse<ListData<EmergencyInstructionData>>) => {
                        return response.data.data.map((d) => EmergencyInstruction.from(d));
                    })
                    .catch(() => []);
            }
            return [];
        },
        staleTime: 1000 * 60 * 60, // 1 hour
        placeholderData: [],
        refetchOnMount: true,
    });

    const instructions = useMemo(
        () => (profile_id ? (instructionQuery.data ?? []) : []),
        [profile_id, instructionQuery],
    );

    const refreshInstructions = async () => {
        await queryClient.invalidateQueries({ queryKey: instructionKey, refetchType: "all" });
    };

    const storeInstructionMutation = useMutation({
        mutationFn: async ({ id, ...payload }: EmergencyInstructionPayload) => {
            const { method, url } = resolveStoreMethod(`profiles/${profile_id}/instructions`, id);

            return await client()
                [method](url, payload)
                .then((response: AxiosResponse<EmergencyInstructionData>) => {
                    return EmergencyInstruction.from(response.data);
                });
        },
        onSuccess: (instruction: EmergencyInstruction) => {
            queryClient.setQueryData(instructionKey, mergeQuery(instruction));
        },
    });
    const storeInstruction = async (payload: EmergencyInstructionPayload) => {
        if (cannot("update", "emergency")) {
            return;
        }

        const existing = instructions.find((i) => i.id === payload.id);

        if (existing) {
            if (!EmergencyInstruction.from(existing).fill(payload).dirty(["description", "photograph_ids"])) {
                return;
            }
        }

        await storeInstructionMutation.mutateAsync(payload);
    };
    const destroyInstructionMutation = useMutation({
        mutationFn: async (id: string) => {
            const url = `profiles/${profile_id}/instructions/${id}`;

            return await client()
                .delete(url)
                .then(() => id);
        },
        onSuccess: (id: string) => {
            queryClient.setQueryData(instructionKey, (cache?: EmergencyInstruction[]) =>
                [...(cache ?? [])].filter((i) => i.id !== id),
            );
        },
    });
    const destroyInstruction = async (id: string) => {
        if (cannot("update", "emergency")) {
            return;
        }

        await destroyInstructionMutation.mutateAsync(id);
    };

    const invitationsKey = useMemo(() => ["invitations", profile_id], [profile_id]);
    const invitationsQuery = useQuery({
        queryKey: invitationsKey,
        queryFn: async () => {
            if (profile_id && can("update", "team")) {
                return await client()
                    .get(`profiles/${profile_id}/invitations`)
                    .then((response: AxiosResponse<ListData<ProfileInvitationData>>) => {
                        return response.data.data;
                    })
                    .catch(() => []);
            }
            return [];
        },
        staleTime: 1000 * 60 * 30, // 30 minutes
        placeholderData: [],
        refetchOnMount: true,
    });

    const canUpdateRole = useCallback(
        (role?: ProfileRole | null) => can(role === ProfileRole.owner ? "assign_owner" : "update", "team"),
        [can],
    );

    const storeInvitationMutation = useMutation({
        mutationFn: async ({ id, ...payload }: InvitationPayload) => {
            const { method, url } = resolveStoreMethod(`profiles/${profile_id}/invitations`, id);

            if (payload.permissions) {
                payload.permissions = new Permissions(payload.permissions).onlySet();
            }

            return await client()
                [method](url, payload)
                .then((response: AxiosResponse<ProfileInvitationData>) => {
                    return response.data;
                });
        },
        onSuccess: (invitation: ProfileInvitationData) => {
            queryClient.setQueryData(invitationsKey, mergeQuery(invitation));
        },
    });
    const destroyInvitationMutation = useMutation({
        mutationFn: async (id: string) => {
            return await deleteQuery(client(), `profiles/${profile_id}/invitations/${id}`, id);
        },
        onSuccess: (id: string) => {
            queryClient.setQueryData(invitationsKey, removeQuery(id));
        },
    });
    const storeInvitation = async (payload: InvitationPayload) => {
        if (cannot("update", "team")) {
            return null;
        }

        const existing = filled(payload.id) ? findTeam(payload.id) : null;

        return await storeInvitationMutation
            .mutateAsync(payload)
            .then((invite) => invite.id)
            .finally(() => {
                if (existing?.emergency_contact || payload.emergency_contact) {
                    refreshContacts();
                }
            });
    };
    const destroyInvitation = async (id: string) => {
        if (cannot("update", "team")) {
            return;
        }

        const existing = findTeam(id);

        await destroyInvitationMutation.mutateAsync(id).finally(() => {
            if (existing?.emergency_contact) {
                refreshContacts();
            }
        });
    };
    const refreshInvitations = async () => {
        await queryClient.invalidateQueries({ queryKey: invitationsKey, refetchType: "all" });
    };
    const invitations = useMemo(
        () => (can("update", "team") ? (invitationsQuery.data ?? []) : []),
        [can, invitationsQuery.data],
    );

    const contactsKey = useMemo(() => ["contacts", profile_id], [profile_id]);
    const contactsQuery = useQuery({
        queryKey: contactsKey,
        queryFn: async () => {
            if (can("view", "emergency")) {
                return await client()
                    .get(`profiles/${profile_id}/contacts`)
                    .then((response: AxiosResponse<ListData<ContactData>>) => {
                        return response.data.data;
                    })
                    .catch(() => []);
            }
            return [];
        },
        staleTime: 1000 * 60 * 30, // 30 minutes
        placeholderData: [],
    });

    const storeContactMutation = useMutation({
        mutationFn: async ({ id, ...payload }: ContactPayload) => {
            const { method, url } = resolveStoreMethod(`profiles/${profile_id}/contacts`, id);

            return await client()
                [method](url, payload)
                .then((response: AxiosResponse<ContactData>) => {
                    return response.data;
                });
        },
        onSuccess: (contact: ContactData) => {
            queryClient.setQueryData(contactsKey, mergeQuery(contact));
        },
    });
    const destroyContactMutation = useMutation({
        mutationFn: async (id: string) => {
            return await deleteQuery(client(), `profiles/${profile_id}/contacts/${id}`, id);
        },
        onSuccess: (id: string) => {
            queryClient.setQueryData(contactsKey, removeQuery(id));
        },
    });
    const storeContact = async (payload: ContactPayload) => {
        if (cannot("update", "emergency")) {
            return null;
        }

        return await storeContactMutation.mutateAsync(payload).then(({ id }) => id ?? null);
    };
    const destroyContact = async (id: string) => {
        if (cannot("update", "emergency")) {
            return;
        }

        await destroyContactMutation.mutateAsync(id);
    };
    const refreshContacts = async () => {
        await queryClient.invalidateQueries({ queryKey: contactsKey, refetchType: "all" });
    };

    const contacts = useMemo(
        () => (can("view", "profile") ? (contactsQuery.data ?? []) : []),
        [can, contactsQuery.data],
    );

    const teamKey = ["team", profile_id];
    const teamQuery = useQuery({
        queryKey: teamKey,
        queryFn: async () => {
            if (profile_id && can("view", "team")) {
                return await client()
                    .get(`profiles/${profile_id}/team`)
                    .then((response: AxiosResponse<ListData<TeamMemberData>>) => {
                        return response.data.data;
                    })
                    .catch(() => []);
            }
            return [];
        },
        staleTime: 1000 * 60 * 30, // 30 minutes
        placeholderData: [],
        refetchOnMount: true,
    });
    const team = useMemo(() => (can("view", "team") ? (teamQuery.data ?? []) : []), [can, teamQuery.data]);

    const canUpdateTeamMember = useCallback(
        (team_member_id: string) => {
            const team_member = team.find(({ id }) => id === team_member_id);

            if (filled(team_member)) {
                return (
                    canUpdateRole(team_member.role) &&
                    team_member.user?.id !== user.id &&
                    (team_member.user?.profile_id !== profile_id || team_member.role !== ProfileRole.owner)
                );
            }

            const invite = invitations.find(({ id }) => id === team_member_id);

            if (filled(invite)) {
                return canUpdateRole(invite.role);
            }

            const contact = contacts.find(({ id }) => id === team_member_id);

            return filled(contact) && canUpdateRole(contact.role);
        },
        [user, team, invitations, contacts, canUpdateRole, profile_id],
    );

    const storeShareMutation = useMutation({
        mutationFn: async (payload: ShareCreatePayload) => {
            payload.permissions = new Permissions(
                payload.permissions,
            ).onlySet() as PartialProfilePermissions<ReadOnlyProfilePermissions>;

            return await client()
                .post(`profiles/${profile_id}/share`, payload)
                .then((response: AxiosResponse<ShareResponsePayload>) => {
                    return response.data;
                });
        },
        onSuccess: ({ url: _url, password: _password, ...member }: ShareResponsePayload) => {
            queryClient.setQueryData(teamKey, mergeQuery(member));
        },
    });
    const storeTeamMutation = useMutation({
        mutationFn: async ({ id, ...payload }: TeamMemberUpdatePayload) => {
            if (payload.permissions) {
                payload.permissions = new Permissions(payload.permissions).onlySet();
            }

            return await client()
                .patch(`profiles/${profile_id}/team/${id}`, payload)
                .then((response: AxiosResponse<TeamMemberData>) => {
                    return response.data;
                });
        },
        onSuccess: (member: TeamMemberData) => {
            queryClient.setQueryData(teamKey, mergeQuery(member));
        },
    });
    const destroyTeamMutation = useMutation({
        mutationFn: async (id: string) => {
            return await client()
                .delete(`profiles/${profile_id}/team/${id}`)
                .then(() => id)
                .catch((error: ApiError) => {
                    if (error.status === 404) {
                        return id;
                    }

                    throw error;
                });
        },
        onSuccess: (id: string) => {
            queryClient.setQueryData(teamKey, (cache?: TeamMemberData[]) => {
                return [...(cache ?? [])].filter((item) => item.id !== id);
            });
        },
    });

    const createShare = async (payload: ShareCreatePayload) => {
        if (cannot("update", "team")) {
            return null;
        }

        return await storeShareMutation.mutateAsync(payload);
    };
    const updateTeamMember = async (payload: TeamMemberUpdatePayload) => {
        if (!canUpdateTeamMember(payload.id)) {
            return;
        }

        const existing = findTeam(payload.id);

        await storeTeamMutation.mutateAsync(payload).finally(() => {
            if (Boolean(existing?.emergency_contact) !== payload.emergency_contact) {
                refreshContacts();
            }
        });
    };
    const destroyTeamMember = async (id: string) => {
        if (!canUpdateTeamMember(id)) {
            return;
        }

        const existing = findTeam(id);

        await destroyTeamMutation.mutateAsync(id).finally(() => {
            if (existing?.emergency_contact) {
                refreshContacts();
            }
        });
    };
    const refreshTeam = async () => {
        await queryClient.invalidateQueries({ queryKey: teamKey, refetchType: "all" });
    };

    const findTeam = useCallback(
        (team_member_id: string) =>
            team.find(({ id }) => id === team_member_id) ??
            invitations.find(({ id }) => id === team_member_id) ??
            contacts.find(({ id }) => id === team_member_id) ??
            null,
        [team, contacts, invitations],
    );

    const is_primary = isPrimaryTeam(profile_id);

    const permission_props: PermissionProps = {
        can,
        canAny,
        cannot,
        cannotAny,
    };

    const value: ProfileState = {
        profile: profile ?? EmptyProfile,
        profile_id: profile_id,
        active: Boolean(profile_id),
        can_update,
        hydrated,
        instructions: instructions,
        cycle,
        invitations,
        team,
        contacts,
        is_primary,
        changeProfile,
        updateProfile,
        updateCycleSettings,
        refreshCycle,
        saveGoals,
        updateAvatar,
        storeInstruction,
        destroyInstruction,
        refreshInstructions,
        canUpdateRole,
        storeInvitation,
        createShare,
        destroyInvitation,
        refreshInvitations,
        storeContact,
        destroyContact,
        refreshContacts,
        canUpdateTeamMember,
        updateTeamMember,
        destroyTeamMember,
        refreshTeam,
        findTeam,
        ...permission_props,
    };

    return (
        <ProfileContext.Provider value={value}>
            {hydrated && profile_id ? (
                <OptionsProvider profile_id={profile_id} {...permission_props}>
                    <TrackableProvider profile_id={profile_id} {...permission_props}>
                        <ReportProvider profile_id={profile_id} {...permission_props}>
                            <GalleryProvider profile_id={profile_id} {...permission_props}>
                                <MedicalProvider profile_id={profile_id} {...permission_props}>
                                    <JournalProvider profile_id={profile_id} cycle={cycle} {...permission_props}>
                                        {children}
                                    </JournalProvider>
                                </MedicalProvider>
                            </GalleryProvider>
                        </ReportProvider>
                    </TrackableProvider>
                </OptionsProvider>
            ) : (
                <>
                    <Only when={loading}>
                        <LoadingMask />
                    </Only>
                    {children}
                </>
            )}
        </ProfileContext.Provider>
    );
}
